/*************************************************************
 * WARNING: This file was automatically generated by script. *
 *************************************************************/

/* eslint-disable @typescript-eslint/no-unused-vars */

import type { CancelTokenSource } from 'axios';
import type { CallApiResult } from '@tmapy/beetlejuice-utils';
import type { QueryArgs } from '../../utils/queryArgs';
import { callApi } from '../../utils/api';
import { queryArgs } from '../../utils/queryArgs';
import type { PaginatedDocListListResponse } from '../types/PaginatedDocListList';
import type { Flow } from '../types/Flow';
import type { LatestCheckState } from '../types/LatestCheckState';
import type { PrejimkaType } from '../types/PrejimkaType';
import type { State } from '../types/State';
import type { SvuCrossNames } from '../types/SvuCrossNames';

import type { UseResource } from '../../hooks/useResource';
import { useResource } from '../../hooks/useResource';

export type DocsListRequest = {} | null | undefined;
export type DocsListResponse = PaginatedDocListListResponse;

export type DocsListQuery = {
  /**
   * ```yaml
   * in: query
   * name: author_full_name
   * schema:
   *   type: string
   * description: Author full name
   * ```
   */
  author_full_name?: string;

  /**
   * ```yaml
   * in: query
   * name: cadasters
   * schema:
   *   type: array
   *   items:
   *     type: integer
   *     maximum: 2147483647
   *     minimum: -2147483648
   * explode: true
   * style: form
   * ```
   */
  cadasters?: number[];

  /**
   * ```yaml
   * in: query
   * name: check_not_completed
   * schema:
   *   type: boolean
   * description: Latest check aborted or interrupted, or the check's R17 not delivered
   * ```
   */
  check_not_completed?: boolean;

  /**
   * ```yaml
   * in: query
   * name: closed__gt
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  closed__gt?: string;

  /**
   * ```yaml
   * in: query
   * name: closed__gte
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  closed__gte?: string;

  /**
   * ```yaml
   * in: query
   * name: closed__lt
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  closed__lt?: string;

  /**
   * ```yaml
   * in: query
   * name: closed__lte
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  closed__lte?: string;

  /**
   * ```yaml
   * in: query
   * name: created__gt
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  created__gt?: string;

  /**
   * ```yaml
   * in: query
   * name: created__gte
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  created__gte?: string;

  /**
   * ```yaml
   * in: query
   * name: created__lt
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  created__lt?: string;

  /**
   * ```yaml
   * in: query
   * name: created__lte
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  created__lte?: string;

  /**
   * ```yaml
   * in: query
   * name: creator_full_name
   * schema:
   *   type: string
   * description: Creator full name
   * ```
   */
  creator_full_name?: string;

  /**
   * ```yaml
   * in: query
   * name: data_origin
   * schema:
   *   type: string
   *   nullable: true
   *   enum:
   *     - GEODETIC_SURVEYING
   *     - LASER_SCANNING
   *     - PHOTOGRAMMETRY
   * description: |-
   *   * `GEODETIC_SURVEYING` - GeodeticSurveying
   *   * `LASER_SCANNING` - Laser scanning
   *   * `PHOTOGRAMMETRY` - Photogrammetry
   * ```
   */
  data_origin?: ('GEODETIC_SURVEYING' | 'LASER_SCANNING' | 'PHOTOGRAMMETRY') | null;

  /**
   * ```yaml
   * in: query
   * name: description__icontains
   * schema:
   *   type: string
   * ```
   */
  description__icontains?: string;

  /**
   * ```yaml
   * in: query
   * name: dmvs_id_podani
   * schema:
   *   type: string
   * ```
   */
  dmvs_id_podani?: string;

  /**
   * ```yaml
   * in: query
   * name: dmvs_id_podani__icontains
   * schema:
   *   type: string
   * ```
   */
  dmvs_id_podani__icontains?: string;

  /**
   * ```yaml
   * in: query
   * name: dmvs_identifikace_zmeny__icontains
   * schema:
   *   type: string
   * ```
   */
  dmvs_identifikace_zmeny__icontains?: string;

  /**
   * ```yaml
   * in: query
   * name: dmvs_source
   * schema:
   *   type: string
   *   nullable: true
   *   enum:
   *     - R10EditaceDti
   *     - R12EditujZps
   *     - R16PrijmiGadAzi
   *     - R16PrijmiGadDtmk
   *     - R16PrijmiGadEditoraZps
   *     - R16PrijmiGadVsp
   * description: |-
   *   * `R10EditaceDti` - R10 editace DTI
   *   * `R12EditujZps` - R12 edituj ZPS
   *   * `R16PrijmiGadAzi` - R16 prijmi GAD AZI
   *   * `R16PrijmiGadDtmk` - R16 prijmi GAD DTMK
   *   * `R16PrijmiGadEditoraZps` - R16 prijmi GAD editora ZPS
   *   * `R16PrijmiGadVsp` - R16 prijmi GAD VSP
   * ```
   */
  dmvs_source?:
    | (
        | 'R10EditaceDti'
        | 'R12EditujZps'
        | 'R16PrijmiGadAzi'
        | 'R16PrijmiGadDtmk'
        | 'R16PrijmiGadEditoraZps'
        | 'R16PrijmiGadVsp'
      )
    | null;

  /**
   * ```yaml
   * in: query
   * name: documentation_type
   * schema:
   *   type: string
   *   nullable: true
   *   enum:
   *     - DSPS
   *     - GP
   *     - SURVEYING
   * description: |-
   *   * `DSPS` - DSPS
   *   * `GP` - GP
   *   * `SURVEYING` - Surveying
   * ```
   */
  documentation_type?: ('DSPS' | 'GP' | 'SURVEYING') | null;

  /**
   * ```yaml
   * in: query
   * name: editor_manager_full_name
   * schema:
   *   type: string
   * description: Editor full name
   * ```
   */
  editor_manager_full_name?: string;

  /**
   * ```yaml
   * in: query
   * name: editor_subjekt_full_name
   * schema:
   *   type: string
   * description: Editor subjekt full name
   * ```
   */
  editor_subjekt_full_name?: string;

  /**
   * ```yaml
   * in: query
   * name: finished__gte
   * schema:
   *   type: string
   *   format: date-time
   * description: Finished states (closed, rejected) větší než
   * ```
   */
  finished__gte?: string;

  /**
   * ```yaml
   * in: query
   * name: finished__lte
   * schema:
   *   type: string
   *   format: date-time
   * description: Finished states (closed, rejected) menší než
   * ```
   */
  finished__lte?: string;

  /**
   * ```yaml
   * in: query
   * name: flow
   * schema:
   *   type: string
   *   nullable: true
   *   enum:
   *     - BB_IMPORT
   *     - CEPRO_IMPORT
   *     - CHECK
   *     - CROSS_OWNER
   *     - CROSS_WORKER
   *     - DMVS_DTI
   *     - DMVS_GAD
   *     - DMVS_GAD_CHECK
   *     - DTM
   *     - DTM_KAD
   *     - GP
   *     - GP_BRNO
   *     - INITIAL_IMPORT
   *     - SVU_CROSS_OWNER
   *     - SVU_CROSS_WORKER
   *     - SVU_OWNER
   *     - SVU_WORKER
   * description: |-
   *   * `BB_IMPORT` - BB_IMPORT
   *   * `CEPRO_IMPORT` - CEPRO_IMPORT
   *   * `INITIAL_IMPORT` - INITIAL_IMPORT
   *   * `DTM` - DTM
   *   * `DTM_KAD` - DTM_KAD
   *   * `GP` - GP
   *   * `CHECK` - CHECK
   *   * `DMVS_GAD_CHECK` - DMVS_GAD_CHECK
   *   * `DMVS_DTI` - DMVS_DTI
   *   * `DMVS_GAD` - DMVS_GAD
   *   * `CROSS_OWNER` - CROSS_OWNER
   *   * `CROSS_WORKER` - CROSS_WORKER
   *   * `SVU_WORKER` - SVU_WORKER
   *   * `SVU_OWNER` - SVU_OWNER
   *   * `SVU_CROSS_WORKER` - SVU_CROSS_WORKER
   *   * `SVU_CROSS_OWNER` - SVU_CROSS_OWNER
   *   * `GP_BRNO` - GP_BRNO
   * ```
   */
  flow?:
    | (
        | 'BB_IMPORT'
        | 'CEPRO_IMPORT'
        | 'CHECK'
        | 'CROSS_OWNER'
        | 'CROSS_WORKER'
        | 'DMVS_DTI'
        | 'DMVS_GAD'
        | 'DMVS_GAD_CHECK'
        | 'DTM'
        | 'DTM_KAD'
        | 'GP'
        | 'GP_BRNO'
        | 'INITIAL_IMPORT'
        | 'SVU_CROSS_OWNER'
        | 'SVU_CROSS_WORKER'
        | 'SVU_OWNER'
        | 'SVU_WORKER'
      )
    | null;

  /**
   * ```yaml
   * in: query
   * name: flow__in
   * schema:
   *   type: array
   *   items:
   *     type: string
   *     nullable: true
   *     enum:
   *       - BB_IMPORT
   *       - CEPRO_IMPORT
   *       - CHECK
   *       - CROSS_OWNER
   *       - CROSS_WORKER
   *       - DMVS_DTI
   *       - DMVS_GAD
   *       - DMVS_GAD_CHECK
   *       - DTM
   *       - DTM_KAD
   *       - GP
   *       - GP_BRNO
   *       - INITIAL_IMPORT
   *       - SVU_CROSS_OWNER
   *       - SVU_CROSS_WORKER
   *       - SVU_OWNER
   *       - SVU_WORKER
   * description: |-
   *   * `BB_IMPORT` - BB_IMPORT
   *   * `CEPRO_IMPORT` - CEPRO_IMPORT
   *   * `INITIAL_IMPORT` - INITIAL_IMPORT
   *   * `DTM` - DTM
   *   * `DTM_KAD` - DTM_KAD
   *   * `GP` - GP
   *   * `CHECK` - CHECK
   *   * `DMVS_GAD_CHECK` - DMVS_GAD_CHECK
   *   * `DMVS_DTI` - DMVS_DTI
   *   * `DMVS_GAD` - DMVS_GAD
   *   * `CROSS_OWNER` - CROSS_OWNER
   *   * `CROSS_WORKER` - CROSS_WORKER
   *   * `SVU_WORKER` - SVU_WORKER
   *   * `SVU_OWNER` - SVU_OWNER
   *   * `SVU_CROSS_WORKER` - SVU_CROSS_WORKER
   *   * `SVU_CROSS_OWNER` - SVU_CROSS_OWNER
   *   * `GP_BRNO` - GP_BRNO
   * explode: true
   * style: form
   * ```
   */
  flow__in?: Flow[];

  /**
   * ```yaml
   * in: query
   * name: flow__not_in
   * schema:
   *   type: array
   *   items:
   *     type: string
   *     nullable: true
   *     enum:
   *       - BB_IMPORT
   *       - CEPRO_IMPORT
   *       - CHECK
   *       - CROSS_OWNER
   *       - CROSS_WORKER
   *       - DMVS_DTI
   *       - DMVS_GAD
   *       - DMVS_GAD_CHECK
   *       - DTM
   *       - DTM_KAD
   *       - GP
   *       - GP_BRNO
   *       - INITIAL_IMPORT
   *       - SVU_CROSS_OWNER
   *       - SVU_CROSS_WORKER
   *       - SVU_OWNER
   *       - SVU_WORKER
   * description: |-
   *   * `BB_IMPORT` - BB_IMPORT
   *   * `CEPRO_IMPORT` - CEPRO_IMPORT
   *   * `INITIAL_IMPORT` - INITIAL_IMPORT
   *   * `DTM` - DTM
   *   * `DTM_KAD` - DTM_KAD
   *   * `GP` - GP
   *   * `CHECK` - CHECK
   *   * `DMVS_GAD_CHECK` - DMVS_GAD_CHECK
   *   * `DMVS_DTI` - DMVS_DTI
   *   * `DMVS_GAD` - DMVS_GAD
   *   * `CROSS_OWNER` - CROSS_OWNER
   *   * `CROSS_WORKER` - CROSS_WORKER
   *   * `SVU_WORKER` - SVU_WORKER
   *   * `SVU_OWNER` - SVU_OWNER
   *   * `SVU_CROSS_WORKER` - SVU_CROSS_WORKER
   *   * `SVU_CROSS_OWNER` - SVU_CROSS_OWNER
   *   * `GP_BRNO` - GP_BRNO
   * explode: true
   * style: form
   * ```
   */
  flow__not_in?: Flow[];

  /**
   * ```yaml
   * in: query
   * name: geodesist_full_name
   * schema:
   *   type: string
   * description: Geodesist full name
   * ```
   */
  geodesist_full_name?: string;

  /**
   * ```yaml
   * in: query
   * name: geometry_plan_number
   * schema:
   *   type: string
   * ```
   */
  geometry_plan_number?: string;

  /**
   * ```yaml
   * in: query
   * name: has_dmvs_source
   * schema:
   *   type: boolean
   * description: Has DMVS source
   * ```
   */
  has_dmvs_source?: boolean;

  /**
   * ```yaml
   * in: query
   * name: has_planimetry
   * schema:
   *   type: boolean
   * ```
   */
  has_planimetry?: boolean;

  /**
   * ```yaml
   * in: query
   * name: has_svu_cross_names
   * schema:
   *   type: boolean
   * description: Has SVU names
   * ```
   */
  has_svu_cross_names?: boolean;

  /**
   * ```yaml
   * in: query
   * name: has_utilities
   * schema:
   *   type: boolean
   * ```
   */
  has_utilities?: boolean;

  /**
   * ```yaml
   * in: query
   * name: id
   * schema:
   *   type: integer
   * ```
   */
  id?: number;

  /**
   * ```yaml
   * in: query
   * name: import_started__gt
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  import_started__gt?: string;

  /**
   * ```yaml
   * in: query
   * name: import_started__gte
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  import_started__gte?: string;

  /**
   * ```yaml
   * in: query
   * name: import_started__lt
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  import_started__lt?: string;

  /**
   * ```yaml
   * in: query
   * name: import_started__lte
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  import_started__lte?: string;

  /**
   * ```yaml
   * in: query
   * name: latest_check_state__in
   * schema:
   *   type: array
   *   items:
   *     type: string
   *     enum:
   *       - ABORTED
   *       - CHECKING
   *       - FAILED
   *       - INTERRUPTED
   *       - PASSED
   *       - WAITING_FOR_CHECKING
   * description: |-
   *   * `WAITING_FOR_CHECKING` - Waiting for checking
   *   * `CHECKING` - Checking
   *   * `ABORTED` - Aborted
   *   * `FAILED` - Failed
   *   * `PASSED` - Passed
   *   * `INTERRUPTED` - Interrupted
   * explode: true
   * style: form
   * ```
   */
  latest_check_state__in?: LatestCheckState[];

  /**
   * ```yaml
   * in: query
   * name: name__icontains
   * schema:
   *   type: string
   * ```
   */
  name__icontains?: string;

  /**
   * ```yaml
   * in: query
   * name: original_id__isnull
   * schema:
   *   type: boolean
   * description: Original ID is null
   * ```
   */
  original_id__isnull?: boolean;

  /**
   * ```yaml
   * in: query
   * name: owner_birth_number__icontains
   * schema:
   *   type: string
   * ```
   */
  owner_birth_number__icontains?: string;

  /**
   * ```yaml
   * in: query
   * name: owner_birth_number__startswith
   * schema:
   *   type: string
   * ```
   */
  owner_birth_number__startswith?: string;

  /**
   * ```yaml
   * in: query
   * name: owner_ico__icontains
   * schema:
   *   type: string
   * ```
   */
  owner_ico__icontains?: string;

  /**
   * ```yaml
   * in: query
   * name: owner_ico__startswith
   * schema:
   *   type: string
   * ```
   */
  owner_ico__startswith?: string;

  /**
   * ```yaml
   * in: query
   * name: owner_name__icontains
   * schema:
   *   type: string
   * ```
   */
  owner_name__icontains?: string;

  /**
   * ```yaml
   * name: page
   * required: false
   * in: query
   * description: A page number within the paginated result set.
   * schema:
   *   type: integer
   * ```
   */
  page?: number;

  /**
   * ```yaml
   * name: page_size
   * required: false
   * in: query
   * description: Number of results to return per page.
   * schema:
   *   type: integer
   * ```
   */
  page_size?: number;

  /**
   * ```yaml
   * in: query
   * name: prejimka_type
   * schema:
   *   type: string
   *   enum:
   *     - BB_IMPORT
   *     - CEPRO_IMPORT
   *     - CHECK
   *     - DMVS_DTI
   *     - DMVS_GAD
   *     - DMVS_GAD_CHECK
   *     - DTM
   *     - DTM_KAD
   *     - GP
   *     - INITIAL_IMPORT
   * description: |-
   *   * `BB_IMPORT` - BB_IMPORT
   *   * `CEPRO_IMPORT` - CEPRO_IMPORT
   *   * `INITIAL_IMPORT` - INITIAL_IMPORT
   *   * `DTM` - DTM
   *   * `DTM_KAD` - DTM_KAD
   *   * `GP` - GP
   *   * `CHECK` - CHECK
   *   * `DMVS_GAD_CHECK` - DMVS_GAD_CHECK
   *   * `DMVS_DTI` - DMVS_DTI
   *   * `DMVS_GAD` - DMVS_GAD
   * ```
   */
  prejimka_type?:
    | 'BB_IMPORT'
    | 'CEPRO_IMPORT'
    | 'CHECK'
    | 'DMVS_DTI'
    | 'DMVS_GAD'
    | 'DMVS_GAD_CHECK'
    | 'DTM'
    | 'DTM_KAD'
    | 'GP'
    | 'INITIAL_IMPORT';

  /**
   * ```yaml
   * in: query
   * name: prejimka_type__in
   * schema:
   *   type: array
   *   items:
   *     type: string
   *     enum:
   *       - BB_IMPORT
   *       - CEPRO_IMPORT
   *       - CHECK
   *       - DMVS_DTI
   *       - DMVS_GAD
   *       - DMVS_GAD_CHECK
   *       - DTM
   *       - DTM_KAD
   *       - GP
   *       - INITIAL_IMPORT
   * description: |-
   *   * `BB_IMPORT` - BB_IMPORT
   *   * `CEPRO_IMPORT` - CEPRO_IMPORT
   *   * `INITIAL_IMPORT` - INITIAL_IMPORT
   *   * `DTM` - DTM
   *   * `DTM_KAD` - DTM_KAD
   *   * `GP` - GP
   *   * `CHECK` - CHECK
   *   * `DMVS_GAD_CHECK` - DMVS_GAD_CHECK
   *   * `DMVS_DTI` - DMVS_DTI
   *   * `DMVS_GAD` - DMVS_GAD
   * explode: true
   * style: form
   * ```
   */
  prejimka_type__in?: PrejimkaType[];

  /**
   * ```yaml
   * in: query
   * name: processor_birth_number__icontains
   * schema:
   *   type: string
   * ```
   */
  processor_birth_number__icontains?: string;

  /**
   * ```yaml
   * in: query
   * name: processor_birth_number__startswith
   * schema:
   *   type: string
   * ```
   */
  processor_birth_number__startswith?: string;

  /**
   * ```yaml
   * in: query
   * name: processor_ico__icontains
   * schema:
   *   type: string
   * ```
   */
  processor_ico__icontains?: string;

  /**
   * ```yaml
   * in: query
   * name: processor_ico__startswith
   * schema:
   *   type: string
   * ```
   */
  processor_ico__startswith?: string;

  /**
   * ```yaml
   * in: query
   * name: processor_name__icontains
   * schema:
   *   type: string
   * ```
   */
  processor_name__icontains?: string;

  /**
   * ```yaml
   * in: query
   * name: receiver_full_name
   * schema:
   *   type: string
   * description: Receiver full name
   * ```
   */
  receiver_full_name?: string;

  /**
   * ```yaml
   * in: query
   * name: rejected__gt
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  rejected__gt?: string;

  /**
   * ```yaml
   * in: query
   * name: rejected__gte
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  rejected__gte?: string;

  /**
   * ```yaml
   * in: query
   * name: rejected__lt
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  rejected__lt?: string;

  /**
   * ```yaml
   * in: query
   * name: rejected__lte
   * schema:
   *   type: string
   *   format: date-time
   * ```
   */
  rejected__lte?: string;

  /**
   * ```yaml
   * in: query
   * name: responsible_person_full_name
   * schema:
   *   type: string
   * description: Responsible person full name
   * ```
   */
  responsible_person_full_name?: string;

  /**
   * ```yaml
   * in: query
   * name: state
   * schema:
   *   type: string
   *   enum:
   *     - ACCEPTED
   *     - CANCELED
   *     - CHECKING_SVU_CROSS_KRAJ
   *     - CHECK_SVU_CROSS_KRAJ_FAILED
   *     - CLOSED
   *     - CLOSED_FINAL
   *     - CLOSE_CHECKS_FAILED
   *     - CLOSE_FAILED
   *     - CLOSE_FINAL_FAILED
   *     - CLOSE_INTERRUPTED
   *     - CLOSING
   *     - CLOSING_FINAL
   *     - CLOSING_READY_TO_SEND_TO_DMVS
   *     - DELEGATED
   *     - DELEGATE_FAILED
   *     - DELEGATE_REJECTED
   *     - DMVS_COMMUNICATION_FAILED
   *     - DRAFT
   *     - DRAFT_CHECKED
   *     - EDITING
   *     - EDIT_REJECTED
   *     - EDIT_SUPERVISING
   *     - EDIT_TAKEOVER_REJECTED
   *     - FAILED_TO_GET_SYNCHRONIZE_DATA
   *     - FAILED_TO_SEND_SYNCHRONIZE_DATA
   *     - IMPORTED
   *     - IMPORTED_WITH_ERRORS
   *     - IMPORTING
   *     - IMPORT_FAILED
   *     - IMPORT_INTERRUPTED
   *     - INPROGRESS
   *     - NOTIFYING_CLOSE
   *     - NOTIFYING_CLOSED
   *     - NOTIFYING_CLOSE_FAILED
   *     - PREPARING_FOR_EDIT
   *     - PREPARING_FOR_EDIT_WITH_ERRORS
   *     - PROCESSED
   *     - PROCESSED_SENT_TO_COUNTERPART
   *     - PROCESSING
   *     - PROCESS_FAILED
   *     - READY_TO_CHECK_SVU_CROSS_KRAJ
   *     - READY_TO_IMPORT
   *     - READY_TO_PROCESS
   *     - READY_TO_SEND_TO_DMVS
   *     - READY_TO_SYNCHRONIZE
   *     - REJECTED
   *     - REJECTED_BY_COUNTERPART
   *     - SENDING_TO_COUNTERPART
   *     - SENDING_TO_DMVS
   *     - SEND_TO_COUNTERPART_FAILED
   *     - SEND_TO_DMVS_FAILED
   *     - SENT_TO_COUNTERPART
   *     - SENT_TO_DMVS
   *     - SIGNED
   *     - SIGNED_FAILED
   *     - SIGNING
   *     - SUBMITTED
   *     - SYNCHRONIZED
   *     - SYNCHRONIZE_FAILED
   *     - SYNCHRONIZING
   *     - TAKEN_OVER
   *     - WAITING_FOR_CHECK_SVU_CROSS_KRAJ
   *     - WAITING_FOR_CLOSE
   *     - WAITING_FOR_CLOSE_FINAL
   *     - WAITING_FOR_IMPORT
   *     - WAITING_FOR_PROCESS
   *     - WAITING_FOR_REJECT
   *     - WAITING_FOR_REJECT_FAILED
   *     - WAITING_FOR_SYNCHRONIZE
   *     - WAITING_FOR_SYNCHRONIZE_DATA
   * description: |-
   *   * `ACCEPTED` - ACCEPTED
   *   * `CANCELED` - CANCELED
   *   * `CLOSED` - CLOSED
   *   * `CLOSED_FINAL` - CLOSED_FINAL
   *   * `CLOSE_CHECKS_FAILED` - CLOSE_CHECKS_FAILED
   *   * `CLOSE_FAILED` - CLOSE_FAILED
   *   * `CLOSE_FINAL_FAILED` - CLOSE_FINAL_FAILED
   *   * `CLOSE_INTERRUPTED` - CLOSE_INTERRUPTED
   *   * `CLOSING_READY_TO_SEND_TO_DMVS` - CLOSING_READY_TO_SEND_TO_DMVS
   *   * `CLOSING` - CLOSING
   *   * `CLOSING_FINAL` - CLOSING_FINAL
   *   * `DELEGATE_FAILED` - DELEGATE_FAILED
   *   * `DELEGATE_REJECTED` - DELEGATE_REJECTED
   *   * `DELEGATED` - DELEGATED
   *   * `DMVS_COMMUNICATION_FAILED` - DMVS_COMMUNICATION_FAILED
   *   * `DRAFT` - DRAFT
   *   * `DRAFT_CHECKED` - DRAFT_CHECKED
   *   * `EDIT_REJECTED` - EDIT_REJECTED
   *   * `EDIT_SUPERVISING` - EDIT_SUPERVISING
   *   * `EDIT_TAKEOVER_REJECTED` - EDIT_TAKEOVER_REJECTED
   *   * `EDITING` - EDITING
   *   * `FAILED_TO_GET_SYNCHRONIZE_DATA` - FAILED_TO_GET_SYNCHRONIZE_DATA
   *   * `FAILED_TO_SEND_SYNCHRONIZE_DATA` - FAILED_TO_SEND_SYNCHRONIZE_DATA
   *   * `IMPORT_FAILED` - IMPORT_FAILED
   *   * `IMPORT_INTERRUPTED` - IMPORT_INTERRUPTED
   *   * `IMPORTED` - IMPORTED
   *   * `IMPORTED_WITH_ERRORS` - IMPORTED_WITH_ERRORS
   *   * `IMPORTING` - IMPORTING
   *   * `INPROGRESS` - INPROGRESS
   *   * `NOTIFYING_CLOSED` - NOTIFYING_CLOSED
   *   * `NOTIFYING_CLOSE` - NOTIFYING_CLOSE
   *   * `NOTIFYING_CLOSE_FAILED` - NOTIFYING_CLOSE_FAILED
   *   * `PREPARING_FOR_EDIT` - PREPARING_FOR_EDIT
   *   * `PREPARING_FOR_EDIT_WITH_ERRORS` - PREPARING_FOR_EDIT_WITH_ERRORS
   *   * `PROCESS_FAILED` - PROCESS_FAILED
   *   * `PROCESSED` - PROCESSED
   *   * `PROCESSED_SENT_TO_COUNTERPART` - PROCESSED_SENT_TO_COUNTERPART
   *   * `PROCESSING` - PROCESSING
   *   * `READY_TO_IMPORT` - READY_TO_IMPORT
   *   * `READY_TO_PROCESS` - READY_TO_PROCESS
   *   * `READY_TO_SEND_TO_DMVS` - READY_TO_SEND_TO_DMVS
   *   * `READY_TO_SYNCHRONIZE` - READY_TO_SYNCHRONIZE
   *   * `READY_TO_CHECK_SVU_CROSS_KRAJ` - READY_TO_CHECK_SVU_CROSS_KRAJ
   *   * `WAITING_FOR_CHECK_SVU_CROSS_KRAJ` - WAITING_FOR_CHECK_SVU_CROSS_KRAJ
   *   * `CHECKING_SVU_CROSS_KRAJ` - CHECKING_SVU_CROSS_KRAJ
   *   * `CHECK_SVU_CROSS_KRAJ_FAILED` - CHECK_SVU_CROSS_KRAJ_FAILED
   *   * `REJECTED` - REJECTED
   *   * `REJECTED_BY_COUNTERPART` - REJECTED_BY_COUNTERPART
   *   * `SEND_TO_DMVS_FAILED` - SEND_TO_DMVS_FAILED
   *   * `SEND_TO_COUNTERPART_FAILED` - SEND_TO_COUNTERPART_FAILED
   *   * `SENDING_TO_DMVS` - SENDING_TO_DMVS
   *   * `SENDING_TO_COUNTERPART` - SENDING_TO_COUNTERPART
   *   * `SENT_TO_COUNTERPART` - SENT_TO_COUNTERPART
   *   * `SENT_TO_DMVS` - SENT_TO_DMVS
   *   * `SIGNING` - SIGNING
   *   * `SIGNED_FAILED` - SIGNED_FAILED
   *   * `SIGNED` - SIGNED
   *   * `SUBMITTED` - SUBMITTED
   *   * `SYNCHRONIZED` - SYNCHRONIZED
   *   * `SYNCHRONIZE_FAILED` - SYNCHRONIZE_FAILED
   *   * `SYNCHRONIZING` - SYNCHRONIZING
   *   * `TAKEN_OVER` - TAKEN_OVER
   *   * `WAITING_FOR_CLOSE` - WAITING_FOR_CLOSE
   *   * `WAITING_FOR_CLOSE_FINAL` - WAITING_FOR_CLOSE_FINAL
   *   * `WAITING_FOR_IMPORT` - WAITING_FOR_IMPORT
   *   * `WAITING_FOR_REJECT` - WAITING_FOR_REJECT
   *   * `WAITING_FOR_REJECT_FAILED` - WAITING_FOR_REJECT_FAILED
   *   * `WAITING_FOR_PROCESS` - WAITING_FOR_PROCESS
   *   * `WAITING_FOR_SYNCHRONIZE` - WAITING_FOR_SYNCHRONIZE
   *   * `WAITING_FOR_SYNCHRONIZE_DATA` - WAITING_FOR_SYNCHRONIZE_DATA
   * ```
   */
  state?:
    | 'ACCEPTED'
    | 'CANCELED'
    | 'CHECKING_SVU_CROSS_KRAJ'
    | 'CHECK_SVU_CROSS_KRAJ_FAILED'
    | 'CLOSED'
    | 'CLOSED_FINAL'
    | 'CLOSE_CHECKS_FAILED'
    | 'CLOSE_FAILED'
    | 'CLOSE_FINAL_FAILED'
    | 'CLOSE_INTERRUPTED'
    | 'CLOSING'
    | 'CLOSING_FINAL'
    | 'CLOSING_READY_TO_SEND_TO_DMVS'
    | 'DELEGATED'
    | 'DELEGATE_FAILED'
    | 'DELEGATE_REJECTED'
    | 'DMVS_COMMUNICATION_FAILED'
    | 'DRAFT'
    | 'DRAFT_CHECKED'
    | 'EDITING'
    | 'EDIT_REJECTED'
    | 'EDIT_SUPERVISING'
    | 'EDIT_TAKEOVER_REJECTED'
    | 'FAILED_TO_GET_SYNCHRONIZE_DATA'
    | 'FAILED_TO_SEND_SYNCHRONIZE_DATA'
    | 'IMPORTED'
    | 'IMPORTED_WITH_ERRORS'
    | 'IMPORTING'
    | 'IMPORT_FAILED'
    | 'IMPORT_INTERRUPTED'
    | 'INPROGRESS'
    | 'NOTIFYING_CLOSE'
    | 'NOTIFYING_CLOSED'
    | 'NOTIFYING_CLOSE_FAILED'
    | 'PREPARING_FOR_EDIT'
    | 'PREPARING_FOR_EDIT_WITH_ERRORS'
    | 'PROCESSED'
    | 'PROCESSED_SENT_TO_COUNTERPART'
    | 'PROCESSING'
    | 'PROCESS_FAILED'
    | 'READY_TO_CHECK_SVU_CROSS_KRAJ'
    | 'READY_TO_IMPORT'
    | 'READY_TO_PROCESS'
    | 'READY_TO_SEND_TO_DMVS'
    | 'READY_TO_SYNCHRONIZE'
    | 'REJECTED'
    | 'REJECTED_BY_COUNTERPART'
    | 'SENDING_TO_COUNTERPART'
    | 'SENDING_TO_DMVS'
    | 'SEND_TO_COUNTERPART_FAILED'
    | 'SEND_TO_DMVS_FAILED'
    | 'SENT_TO_COUNTERPART'
    | 'SENT_TO_DMVS'
    | 'SIGNED'
    | 'SIGNED_FAILED'
    | 'SIGNING'
    | 'SUBMITTED'
    | 'SYNCHRONIZED'
    | 'SYNCHRONIZE_FAILED'
    | 'SYNCHRONIZING'
    | 'TAKEN_OVER'
    | 'WAITING_FOR_CHECK_SVU_CROSS_KRAJ'
    | 'WAITING_FOR_CLOSE'
    | 'WAITING_FOR_CLOSE_FINAL'
    | 'WAITING_FOR_IMPORT'
    | 'WAITING_FOR_PROCESS'
    | 'WAITING_FOR_REJECT'
    | 'WAITING_FOR_REJECT_FAILED'
    | 'WAITING_FOR_SYNCHRONIZE'
    | 'WAITING_FOR_SYNCHRONIZE_DATA';

  /**
   * ```yaml
   * in: query
   * name: state__in
   * schema:
   *   type: array
   *   items:
   *     type: string
   *     enum:
   *       - ACCEPTED
   *       - CANCELED
   *       - CHECKING_SVU_CROSS_KRAJ
   *       - CHECK_SVU_CROSS_KRAJ_FAILED
   *       - CLOSED
   *       - CLOSED_FINAL
   *       - CLOSE_CHECKS_FAILED
   *       - CLOSE_FAILED
   *       - CLOSE_FINAL_FAILED
   *       - CLOSE_INTERRUPTED
   *       - CLOSING
   *       - CLOSING_FINAL
   *       - CLOSING_READY_TO_SEND_TO_DMVS
   *       - DELEGATED
   *       - DELEGATE_FAILED
   *       - DELEGATE_REJECTED
   *       - DMVS_COMMUNICATION_FAILED
   *       - DRAFT
   *       - DRAFT_CHECKED
   *       - EDITING
   *       - EDIT_REJECTED
   *       - EDIT_SUPERVISING
   *       - EDIT_TAKEOVER_REJECTED
   *       - FAILED_TO_GET_SYNCHRONIZE_DATA
   *       - FAILED_TO_SEND_SYNCHRONIZE_DATA
   *       - IMPORTED
   *       - IMPORTED_WITH_ERRORS
   *       - IMPORTING
   *       - IMPORT_FAILED
   *       - IMPORT_INTERRUPTED
   *       - INPROGRESS
   *       - NOTIFYING_CLOSE
   *       - NOTIFYING_CLOSED
   *       - NOTIFYING_CLOSE_FAILED
   *       - PREPARING_FOR_EDIT
   *       - PREPARING_FOR_EDIT_WITH_ERRORS
   *       - PROCESSED
   *       - PROCESSED_SENT_TO_COUNTERPART
   *       - PROCESSING
   *       - PROCESS_FAILED
   *       - READY_TO_CHECK_SVU_CROSS_KRAJ
   *       - READY_TO_IMPORT
   *       - READY_TO_PROCESS
   *       - READY_TO_SEND_TO_DMVS
   *       - READY_TO_SYNCHRONIZE
   *       - REJECTED
   *       - REJECTED_BY_COUNTERPART
   *       - SENDING_TO_COUNTERPART
   *       - SENDING_TO_DMVS
   *       - SEND_TO_COUNTERPART_FAILED
   *       - SEND_TO_DMVS_FAILED
   *       - SENT_TO_COUNTERPART
   *       - SENT_TO_DMVS
   *       - SIGNED
   *       - SIGNED_FAILED
   *       - SIGNING
   *       - SUBMITTED
   *       - SYNCHRONIZED
   *       - SYNCHRONIZE_FAILED
   *       - SYNCHRONIZING
   *       - TAKEN_OVER
   *       - WAITING_FOR_CHECK_SVU_CROSS_KRAJ
   *       - WAITING_FOR_CLOSE
   *       - WAITING_FOR_CLOSE_FINAL
   *       - WAITING_FOR_IMPORT
   *       - WAITING_FOR_PROCESS
   *       - WAITING_FOR_REJECT
   *       - WAITING_FOR_REJECT_FAILED
   *       - WAITING_FOR_SYNCHRONIZE
   *       - WAITING_FOR_SYNCHRONIZE_DATA
   * description: |-
   *   * `ACCEPTED` - ACCEPTED
   *   * `CANCELED` - CANCELED
   *   * `CLOSED` - CLOSED
   *   * `CLOSED_FINAL` - CLOSED_FINAL
   *   * `CLOSE_CHECKS_FAILED` - CLOSE_CHECKS_FAILED
   *   * `CLOSE_FAILED` - CLOSE_FAILED
   *   * `CLOSE_FINAL_FAILED` - CLOSE_FINAL_FAILED
   *   * `CLOSE_INTERRUPTED` - CLOSE_INTERRUPTED
   *   * `CLOSING_READY_TO_SEND_TO_DMVS` - CLOSING_READY_TO_SEND_TO_DMVS
   *   * `CLOSING` - CLOSING
   *   * `CLOSING_FINAL` - CLOSING_FINAL
   *   * `DELEGATE_FAILED` - DELEGATE_FAILED
   *   * `DELEGATE_REJECTED` - DELEGATE_REJECTED
   *   * `DELEGATED` - DELEGATED
   *   * `DMVS_COMMUNICATION_FAILED` - DMVS_COMMUNICATION_FAILED
   *   * `DRAFT` - DRAFT
   *   * `DRAFT_CHECKED` - DRAFT_CHECKED
   *   * `EDIT_REJECTED` - EDIT_REJECTED
   *   * `EDIT_SUPERVISING` - EDIT_SUPERVISING
   *   * `EDIT_TAKEOVER_REJECTED` - EDIT_TAKEOVER_REJECTED
   *   * `EDITING` - EDITING
   *   * `FAILED_TO_GET_SYNCHRONIZE_DATA` - FAILED_TO_GET_SYNCHRONIZE_DATA
   *   * `FAILED_TO_SEND_SYNCHRONIZE_DATA` - FAILED_TO_SEND_SYNCHRONIZE_DATA
   *   * `IMPORT_FAILED` - IMPORT_FAILED
   *   * `IMPORT_INTERRUPTED` - IMPORT_INTERRUPTED
   *   * `IMPORTED` - IMPORTED
   *   * `IMPORTED_WITH_ERRORS` - IMPORTED_WITH_ERRORS
   *   * `IMPORTING` - IMPORTING
   *   * `INPROGRESS` - INPROGRESS
   *   * `NOTIFYING_CLOSED` - NOTIFYING_CLOSED
   *   * `NOTIFYING_CLOSE` - NOTIFYING_CLOSE
   *   * `NOTIFYING_CLOSE_FAILED` - NOTIFYING_CLOSE_FAILED
   *   * `PREPARING_FOR_EDIT` - PREPARING_FOR_EDIT
   *   * `PREPARING_FOR_EDIT_WITH_ERRORS` - PREPARING_FOR_EDIT_WITH_ERRORS
   *   * `PROCESS_FAILED` - PROCESS_FAILED
   *   * `PROCESSED` - PROCESSED
   *   * `PROCESSED_SENT_TO_COUNTERPART` - PROCESSED_SENT_TO_COUNTERPART
   *   * `PROCESSING` - PROCESSING
   *   * `READY_TO_IMPORT` - READY_TO_IMPORT
   *   * `READY_TO_PROCESS` - READY_TO_PROCESS
   *   * `READY_TO_SEND_TO_DMVS` - READY_TO_SEND_TO_DMVS
   *   * `READY_TO_SYNCHRONIZE` - READY_TO_SYNCHRONIZE
   *   * `READY_TO_CHECK_SVU_CROSS_KRAJ` - READY_TO_CHECK_SVU_CROSS_KRAJ
   *   * `WAITING_FOR_CHECK_SVU_CROSS_KRAJ` - WAITING_FOR_CHECK_SVU_CROSS_KRAJ
   *   * `CHECKING_SVU_CROSS_KRAJ` - CHECKING_SVU_CROSS_KRAJ
   *   * `CHECK_SVU_CROSS_KRAJ_FAILED` - CHECK_SVU_CROSS_KRAJ_FAILED
   *   * `REJECTED` - REJECTED
   *   * `REJECTED_BY_COUNTERPART` - REJECTED_BY_COUNTERPART
   *   * `SEND_TO_DMVS_FAILED` - SEND_TO_DMVS_FAILED
   *   * `SEND_TO_COUNTERPART_FAILED` - SEND_TO_COUNTERPART_FAILED
   *   * `SENDING_TO_DMVS` - SENDING_TO_DMVS
   *   * `SENDING_TO_COUNTERPART` - SENDING_TO_COUNTERPART
   *   * `SENT_TO_COUNTERPART` - SENT_TO_COUNTERPART
   *   * `SENT_TO_DMVS` - SENT_TO_DMVS
   *   * `SIGNING` - SIGNING
   *   * `SIGNED_FAILED` - SIGNED_FAILED
   *   * `SIGNED` - SIGNED
   *   * `SUBMITTED` - SUBMITTED
   *   * `SYNCHRONIZED` - SYNCHRONIZED
   *   * `SYNCHRONIZE_FAILED` - SYNCHRONIZE_FAILED
   *   * `SYNCHRONIZING` - SYNCHRONIZING
   *   * `TAKEN_OVER` - TAKEN_OVER
   *   * `WAITING_FOR_CLOSE` - WAITING_FOR_CLOSE
   *   * `WAITING_FOR_CLOSE_FINAL` - WAITING_FOR_CLOSE_FINAL
   *   * `WAITING_FOR_IMPORT` - WAITING_FOR_IMPORT
   *   * `WAITING_FOR_REJECT` - WAITING_FOR_REJECT
   *   * `WAITING_FOR_REJECT_FAILED` - WAITING_FOR_REJECT_FAILED
   *   * `WAITING_FOR_PROCESS` - WAITING_FOR_PROCESS
   *   * `WAITING_FOR_SYNCHRONIZE` - WAITING_FOR_SYNCHRONIZE
   *   * `WAITING_FOR_SYNCHRONIZE_DATA` - WAITING_FOR_SYNCHRONIZE_DATA
   * explode: true
   * style: form
   * ```
   */
  state__in?: State[];

  /**
   * ```yaml
   * in: query
   * name: state__not_in
   * schema:
   *   type: array
   *   items:
   *     type: string
   *     enum:
   *       - ACCEPTED
   *       - CANCELED
   *       - CHECKING_SVU_CROSS_KRAJ
   *       - CHECK_SVU_CROSS_KRAJ_FAILED
   *       - CLOSED
   *       - CLOSED_FINAL
   *       - CLOSE_CHECKS_FAILED
   *       - CLOSE_FAILED
   *       - CLOSE_FINAL_FAILED
   *       - CLOSE_INTERRUPTED
   *       - CLOSING
   *       - CLOSING_FINAL
   *       - CLOSING_READY_TO_SEND_TO_DMVS
   *       - DELEGATED
   *       - DELEGATE_FAILED
   *       - DELEGATE_REJECTED
   *       - DMVS_COMMUNICATION_FAILED
   *       - DRAFT
   *       - DRAFT_CHECKED
   *       - EDITING
   *       - EDIT_REJECTED
   *       - EDIT_SUPERVISING
   *       - EDIT_TAKEOVER_REJECTED
   *       - FAILED_TO_GET_SYNCHRONIZE_DATA
   *       - FAILED_TO_SEND_SYNCHRONIZE_DATA
   *       - IMPORTED
   *       - IMPORTED_WITH_ERRORS
   *       - IMPORTING
   *       - IMPORT_FAILED
   *       - IMPORT_INTERRUPTED
   *       - INPROGRESS
   *       - NOTIFYING_CLOSE
   *       - NOTIFYING_CLOSED
   *       - NOTIFYING_CLOSE_FAILED
   *       - PREPARING_FOR_EDIT
   *       - PREPARING_FOR_EDIT_WITH_ERRORS
   *       - PROCESSED
   *       - PROCESSED_SENT_TO_COUNTERPART
   *       - PROCESSING
   *       - PROCESS_FAILED
   *       - READY_TO_CHECK_SVU_CROSS_KRAJ
   *       - READY_TO_IMPORT
   *       - READY_TO_PROCESS
   *       - READY_TO_SEND_TO_DMVS
   *       - READY_TO_SYNCHRONIZE
   *       - REJECTED
   *       - REJECTED_BY_COUNTERPART
   *       - SENDING_TO_COUNTERPART
   *       - SENDING_TO_DMVS
   *       - SEND_TO_COUNTERPART_FAILED
   *       - SEND_TO_DMVS_FAILED
   *       - SENT_TO_COUNTERPART
   *       - SENT_TO_DMVS
   *       - SIGNED
   *       - SIGNED_FAILED
   *       - SIGNING
   *       - SUBMITTED
   *       - SYNCHRONIZED
   *       - SYNCHRONIZE_FAILED
   *       - SYNCHRONIZING
   *       - TAKEN_OVER
   *       - WAITING_FOR_CHECK_SVU_CROSS_KRAJ
   *       - WAITING_FOR_CLOSE
   *       - WAITING_FOR_CLOSE_FINAL
   *       - WAITING_FOR_IMPORT
   *       - WAITING_FOR_PROCESS
   *       - WAITING_FOR_REJECT
   *       - WAITING_FOR_REJECT_FAILED
   *       - WAITING_FOR_SYNCHRONIZE
   *       - WAITING_FOR_SYNCHRONIZE_DATA
   * description: |-
   *   * `ACCEPTED` - ACCEPTED
   *   * `CANCELED` - CANCELED
   *   * `CLOSED` - CLOSED
   *   * `CLOSED_FINAL` - CLOSED_FINAL
   *   * `CLOSE_CHECKS_FAILED` - CLOSE_CHECKS_FAILED
   *   * `CLOSE_FAILED` - CLOSE_FAILED
   *   * `CLOSE_FINAL_FAILED` - CLOSE_FINAL_FAILED
   *   * `CLOSE_INTERRUPTED` - CLOSE_INTERRUPTED
   *   * `CLOSING_READY_TO_SEND_TO_DMVS` - CLOSING_READY_TO_SEND_TO_DMVS
   *   * `CLOSING` - CLOSING
   *   * `CLOSING_FINAL` - CLOSING_FINAL
   *   * `DELEGATE_FAILED` - DELEGATE_FAILED
   *   * `DELEGATE_REJECTED` - DELEGATE_REJECTED
   *   * `DELEGATED` - DELEGATED
   *   * `DMVS_COMMUNICATION_FAILED` - DMVS_COMMUNICATION_FAILED
   *   * `DRAFT` - DRAFT
   *   * `DRAFT_CHECKED` - DRAFT_CHECKED
   *   * `EDIT_REJECTED` - EDIT_REJECTED
   *   * `EDIT_SUPERVISING` - EDIT_SUPERVISING
   *   * `EDIT_TAKEOVER_REJECTED` - EDIT_TAKEOVER_REJECTED
   *   * `EDITING` - EDITING
   *   * `FAILED_TO_GET_SYNCHRONIZE_DATA` - FAILED_TO_GET_SYNCHRONIZE_DATA
   *   * `FAILED_TO_SEND_SYNCHRONIZE_DATA` - FAILED_TO_SEND_SYNCHRONIZE_DATA
   *   * `IMPORT_FAILED` - IMPORT_FAILED
   *   * `IMPORT_INTERRUPTED` - IMPORT_INTERRUPTED
   *   * `IMPORTED` - IMPORTED
   *   * `IMPORTED_WITH_ERRORS` - IMPORTED_WITH_ERRORS
   *   * `IMPORTING` - IMPORTING
   *   * `INPROGRESS` - INPROGRESS
   *   * `NOTIFYING_CLOSED` - NOTIFYING_CLOSED
   *   * `NOTIFYING_CLOSE` - NOTIFYING_CLOSE
   *   * `NOTIFYING_CLOSE_FAILED` - NOTIFYING_CLOSE_FAILED
   *   * `PREPARING_FOR_EDIT` - PREPARING_FOR_EDIT
   *   * `PREPARING_FOR_EDIT_WITH_ERRORS` - PREPARING_FOR_EDIT_WITH_ERRORS
   *   * `PROCESS_FAILED` - PROCESS_FAILED
   *   * `PROCESSED` - PROCESSED
   *   * `PROCESSED_SENT_TO_COUNTERPART` - PROCESSED_SENT_TO_COUNTERPART
   *   * `PROCESSING` - PROCESSING
   *   * `READY_TO_IMPORT` - READY_TO_IMPORT
   *   * `READY_TO_PROCESS` - READY_TO_PROCESS
   *   * `READY_TO_SEND_TO_DMVS` - READY_TO_SEND_TO_DMVS
   *   * `READY_TO_SYNCHRONIZE` - READY_TO_SYNCHRONIZE
   *   * `READY_TO_CHECK_SVU_CROSS_KRAJ` - READY_TO_CHECK_SVU_CROSS_KRAJ
   *   * `WAITING_FOR_CHECK_SVU_CROSS_KRAJ` - WAITING_FOR_CHECK_SVU_CROSS_KRAJ
   *   * `CHECKING_SVU_CROSS_KRAJ` - CHECKING_SVU_CROSS_KRAJ
   *   * `CHECK_SVU_CROSS_KRAJ_FAILED` - CHECK_SVU_CROSS_KRAJ_FAILED
   *   * `REJECTED` - REJECTED
   *   * `REJECTED_BY_COUNTERPART` - REJECTED_BY_COUNTERPART
   *   * `SEND_TO_DMVS_FAILED` - SEND_TO_DMVS_FAILED
   *   * `SEND_TO_COUNTERPART_FAILED` - SEND_TO_COUNTERPART_FAILED
   *   * `SENDING_TO_DMVS` - SENDING_TO_DMVS
   *   * `SENDING_TO_COUNTERPART` - SENDING_TO_COUNTERPART
   *   * `SENT_TO_COUNTERPART` - SENT_TO_COUNTERPART
   *   * `SENT_TO_DMVS` - SENT_TO_DMVS
   *   * `SIGNING` - SIGNING
   *   * `SIGNED_FAILED` - SIGNED_FAILED
   *   * `SIGNED` - SIGNED
   *   * `SUBMITTED` - SUBMITTED
   *   * `SYNCHRONIZED` - SYNCHRONIZED
   *   * `SYNCHRONIZE_FAILED` - SYNCHRONIZE_FAILED
   *   * `SYNCHRONIZING` - SYNCHRONIZING
   *   * `TAKEN_OVER` - TAKEN_OVER
   *   * `WAITING_FOR_CLOSE` - WAITING_FOR_CLOSE
   *   * `WAITING_FOR_CLOSE_FINAL` - WAITING_FOR_CLOSE_FINAL
   *   * `WAITING_FOR_IMPORT` - WAITING_FOR_IMPORT
   *   * `WAITING_FOR_REJECT` - WAITING_FOR_REJECT
   *   * `WAITING_FOR_REJECT_FAILED` - WAITING_FOR_REJECT_FAILED
   *   * `WAITING_FOR_PROCESS` - WAITING_FOR_PROCESS
   *   * `WAITING_FOR_SYNCHRONIZE` - WAITING_FOR_SYNCHRONIZE
   *   * `WAITING_FOR_SYNCHRONIZE_DATA` - WAITING_FOR_SYNCHRONIZE_DATA
   * explode: true
   * style: form
   * ```
   */
  state__not_in?: State[];

  /**
   * ```yaml
   * in: query
   * name: subjekt_full_name
   * schema:
   *   type: string
   * description: Subjekt full name
   * ```
   */
  subjekt_full_name?: string;

  /**
   * ```yaml
   * in: query
   * name: svu_cross_names__in
   * schema:
   *   type: array
   *   items:
   *     type: string
   *     enum:
   *       - JIHOCESKY_KRAJ
   *       - JIHOMORAVSKY_KRAJ
   *       - KARLOVARSKY_KRAJ
   *       - KRAJ_VYSOCINA
   *       - KRALOVEHRADECKY_KRAJ
   *       - LIBERECKY_KRAJ
   *       - MORAVSKOSLEZSKY_KRAJ
   *       - OLOMOUCKY_KRAJ
   *       - PARDUBICKY_KRAJ
   *       - PLZENSKY_KRAJ
   *       - REDITELSTVI_SILNIC_A_DALNIC
   *       - SPRAVA_ZELEZNIC
   *       - STREDOCESKY_KRAJ
   *       - USTECKY_KRAJ
   *       - ZLINSKY_KRAJ
   * description: |-
   *   SVU cross names
   *
   *   * `REDITELSTVI_SILNIC_A_DALNIC` - Ředitelství silnic a dálnic s. p.
   *   * `SPRAVA_ZELEZNIC` - Správa železnic, státní organizace
   *   * `STREDOCESKY_KRAJ` - Středočeský kraj
   *   * `JIHOCESKY_KRAJ` - Jihočeský kraj
   *   * `PLZENSKY_KRAJ` - Plzeňský kraj
   *   * `KARLOVARSKY_KRAJ` - Karlovarský kraj
   *   * `USTECKY_KRAJ` - Ústecký kraj
   *   * `LIBERECKY_KRAJ` - Liberecký kraj
   *   * `KRALOVEHRADECKY_KRAJ` - Královéhradecký kraj
   *   * `PARDUBICKY_KRAJ` - Pardubický kraj
   *   * `KRAJ_VYSOCINA` - Kraj Vysočina
   *   * `JIHOMORAVSKY_KRAJ` - Jihomoravský kraj
   *   * `OLOMOUCKY_KRAJ` - Olomoucký kraj
   *   * `ZLINSKY_KRAJ` - Zlínský kraj
   *   * `MORAVSKOSLEZSKY_KRAJ` - Moravskoslezský kraj
   * explode: true
   * style: form
   * ```
   */
  svu_cross_names__in?: SvuCrossNames[];

  /**
   * ```yaml
   * in: query
   * name: utilities_type
   * schema:
   *   type: string
   *   nullable: true
   *   enum:
   *     - ALL_UTILITIES
   *     - ELECTRONIC_COMMUNICATION
   *     - HEAT_PIPE
   *     - PIPELINE
   *     - POWER_LINE
   *     - PRODUCT_PIPELINE
   *     - SEWERAGE
   *     - SOIL_AMELIORATION
   *     - TUBE_MAIL
   *     - WASTE_MANAGEMENT
   *     - WATER_INLETS
   *     - WATER_SUPPLY
   * description: |-
   *   * `POWER_LINE` - Elektrické vedení
   *   * `ELECTRONIC_COMMUNICATION` - Elektronické komunikace
   *   * `PIPELINE` - Plynovod
   *   * `WATER_SUPPLY` - Vodovod
   *   * `WATER_INLETS` - Přivaděče vody
   *   * `SEWERAGE` - Kanalizace
   *   * `PRODUCT_PIPELINE` - Produktovod
   *   * `HEAT_PIPE` - Teplovod
   *   * `TUBE_MAIL` - Potrubní pošta
   *   * `WASTE_MANAGEMENT` - Odpadové hospodářství
   *   * `SOIL_AMELIORATION` - Meliorace
   *   * `ALL_UTILITIES` - Vše
   * ```
   */
  utilities_type?:
    | (
        | 'ALL_UTILITIES'
        | 'ELECTRONIC_COMMUNICATION'
        | 'HEAT_PIPE'
        | 'PIPELINE'
        | 'POWER_LINE'
        | 'PRODUCT_PIPELINE'
        | 'SEWERAGE'
        | 'SOIL_AMELIORATION'
        | 'TUBE_MAIL'
        | 'WASTE_MANAGEMENT'
        | 'WATER_INLETS'
        | 'WATER_SUPPLY'
      )
    | null;
};

/**
 * GET /dmx-server/docs/
 *
 * ```yaml
 * operationId: docs_list
 * description: >-
 *   You can order docs using GET param ordering_state
 *
 *   example:
 *
 *   http://localhost/dmx-server/docs_reduced/?ordering_state=CANCELED,SUBMITTED,REJECTED,ACCEPTED
 *
 *   At first all CANCELED docs, then all with state SUBMITTED, then REJECTED, etc.
 * tags:
 *   - docs
 * security:
 *   - jwtAuth: []
 *   - cookieAuth: []
 * responses:
 *   '200':
 *     content:
 *       application/json:
 *         schema:
 *           $ref: '#/components/schemas/PaginatedDocListList'
 *     description: ''
 * ```
 */
export const docsListOperation = (rest?: DocsListQuery | null) =>
  ({
    method: 'GET',
    path: `/dmx-server/docs/${queryArgs(rest as QueryArgs)}`,
  }) as {
    method: 'GET';
    path: string & { _templateUrl: '/dmx-server/docs/' };
  };

/**
 * GET /dmx-server/docs/
 *
 * ```yaml
 * operationId: docs_list
 * description: >-
 *   You can order docs using GET param ordering_state
 *
 *   example:
 *
 *   http://localhost/dmx-server/docs_reduced/?ordering_state=CANCELED,SUBMITTED,REJECTED,ACCEPTED
 *
 *   At first all CANCELED docs, then all with state SUBMITTED, then REJECTED, etc.
 * tags:
 *   - docs
 * security:
 *   - jwtAuth: []
 *   - cookieAuth: []
 * responses:
 *   '200':
 *     content:
 *       application/json:
 *         schema:
 *           $ref: '#/components/schemas/PaginatedDocListList'
 *     description: ''
 * ```
 */
export const docsList = async ({
  query,
  request,
  onUploadProgress,
  cancelToken,
}: {
  query?: DocsListQuery;
  request?: {} | null;
  onUploadProgress?: null | ((progressEvent: any) => void);
  cancelToken?: null | CancelTokenSource;
}): Promise<CallApiResult<PaginatedDocListListResponse>> => {
  const operation = docsListOperation(query);
  return callApi(
    `${globalThis.DMX_CONFIG.server}${operation.path}`,
    'GET',
    request,
    onUploadProgress,
    cancelToken,
  );
};

/**
 * GET /dmx-server/docs/
 */
export const useDocsListResource = (
  query: DocsListQuery | null,
  useCached?: boolean,
): UseResource<DocsListResponse> => {
  return useResource<DocsListResponse>({
    path: query === null ? null : docsListOperation(query).path,
    apiUrl: globalThis.DMX_CONFIG.server,
    useCached,
  });
};
